// CSS Modules - component class name prefix: ResultList__

<template>
  <div :class="[$style.contentGrid, 'SubPage__contentGrid']">
    <ElasticSearch
      :config="config"
      :model="elasticSearchModel"
      @submitted="startNewSearch"
    />
    <p v-if="hint !== hintEnum.EMPTY" :class="$style.info">
      <em class="hlt1">{{ config.textPrefix }}: </em>
      <span v-html="computedHint"></span>
    </p>
    <p v-if="!!articleNumberSearchResult.articleNumber" :class="$style.info">
      <em class="hlt1">{{ config.hintPrefix }}: </em>
      <span>{{
        `"${articleNumberSearchResult.articleNumber}" ${config.hintArticle} "${
          articleNumberSearchResult.productType
        }"`
      }}</span>
    </p>
    <p v-if="!!articleNumberSearchResult.productType" :class="$style.info">
      <em class="hlt1">{{ config.hintPrefix }}: </em>
      <span v-html="config.hintArticleInShop"></span>
    </p>
    <p v-if="totalHits > 0" :class="$style.info" v-html="resultsText"></p>
    <div class="columns">
      <div class="column is-one-quarter">
        <ResultFilter
          :rangeSize="rangeSize"
          :filterResetText="config.filterResetText"
          :filters="filters"
          :terms="terms"
          :results="results"
          @filtered="displayResults"
        />
      </div>
      <div class="column is-three-quarters">
        <div class="columns is-multiline">
          <ResultTeaserArticle
            v-if="!!articleNumberSearchResult.articleNumber"
            :articleNumber="articleNumberSearchResult.articleNumber"
            :result="displayedResults"
            :bgImgUrl="config.bgImgUrl"
            :shopBaseUrl="config.shopBaseUrl"
            class="column is-full"
          />
          <div
            v-for="(result, index) in displayedResults"
            :key="index"
            class="column is-full"
          >
            <ResultTeaser
              :key="`resultTeaserKey_${JSON.stringify(result)}`"
              :result="result"
              :webBaseUrl="config.webBaseUrl"
              :shopBaseUrl="config.shopBaseUrl"
			  :textfieldOfActivity="config.textfieldOfActivity"
			  :textLocation="config.textLocation"
			  :textCountry="config.textCountry"
			  :textTargetGroup="config.textTargetGroup"
			  :textEmployment="config.textEmployment"
			  :searchMicrosite="config.searchMicrosite"
			  :terms="terms"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ElasticSearch from "../ElasticSearch/ElasticSearch";
import ResultTeaser from "../ResultTeaser/ResultTeaser";
import ResultTeaserArticle from "../ResultTeaserArticle/ResultTeaserArticle";
import ResultFilter from "../ResultFilter/ResultFilter";
import * as Storage from "../../shared/Storage";
import getSearchResultFromUrl from "../../xhr/search/getSearchResultFromUrl";

const PATTERN_ARTICLE_NUMBER = "^[0-9]{6,8}$";
const hintEnum = {
  MIN_LENGTH: "MIN_LENGTH",
  NO_RESULT: "NO_RESULT",
  NONE: "NONE"
};

export default {
  name: "ResultList",
  components: {
    ElasticSearch,
    ResultTeaser,
    ResultTeaserArticle,
    ResultFilter
  },
  props: {
    config: {
      type: Object,
      default: () => {}
    },
    rangeSize: {
      type: Number,
      default: 25
    }
  },
  data() {
    return {
      terms: [],
      displayedResults: [],
      totalHits: 0,
      results: [],
      filters: [],
      failedSearchRequests: {
        terms: null
      },
      range: {
        from: 0
      },
      loading: false,
      useCategorySearch: false,
      elasticSearchModel: [],
      searchContainer: window.searchContainer,
      disableExternalDocuments: window.disableExternalDocuments,
      articleNumberSearchResult: {
        articleNumber: null,
        productType: null
      },
      memorizedLabels: {}, // in case of scolling result has no labels, so we need this cache
      hint: hintEnum.EMPTY,
      hintEnum
    };
  },
  computed: {
    resultsText: function() {
      return this.config.resultsText.replace(
        /{{result}}/g,
        `<strong>${this.totalHits}</strong>`
      );
    },
    computedHint: function() {
      return this.hint === hintEnum.MIN_LENGTH
        ? this.config.textMinLength
        : this.hint === hintEnum.NO_RESULT
        ? this.config.textNoResult.replace(
            /{{searchPhrase}}/g,
            `<strong>${ this.$escapeHTML(this.terms.join(" "))}</strong>`
          )
        : "";
    }
  },
  methods: {
    checkFailedSearches(onMount) {
      let failedSearchRequests = Storage.getLocalStorage(
        "failedSearchRequests",
        true
      );
      if (failedSearchRequests && failedSearchRequests.terms) {
        if (
          failedSearchRequests.terms.length > 1 &&
          !onMount &&
          this.config.emptySearchUrl
        ) {
          window.setTimeout(() => {
            window.location = this.config.emptySearchUrl;
          }, 200);
        } else {
          this.failedSearchRequests.terms = failedSearchRequests.terms;
        }
      }
    },
    getPotentialArticleNumber() {
      const articleNumberRegEx = new RegExp(PATTERN_ARTICLE_NUMBER, "gm");
      return this.terms.find(term => !!articleNumberRegEx.exec(term));
    },
    initSearch() {
      const queries = window.location.search.substring(1).split("&");
      let queryString = [];
      let decodedTerms = [];
      if (queries) {
        for (const query of queries) {
          if (query.includes("wika-search")) {
            queryString = query.split("=")[1];
            this.terms = queryString.split("+");
            for (const term of this.terms) {
              decodedTerms.push(decodeURI(term));
            }

            this.elasticSearchModel = decodedTerms;

            if (decodedTerms.length === 1 && decodedTerms[0].length < 2) {
              this.hint = this.hintEnum.MIN_LENGTH;
              return;
            }

            this.getSearchResults(this.elasticSearchModel);
          }
        }
      }
    },
    initScroll() {
      const distanceToStartLoad = 300;
      window.onscroll = () => {
        let bottomOfWindow =
          document.documentElement.clientHeight +
            document.documentElement.scrollTop +
            distanceToStartLoad >=
          document.documentElement.scrollHeight;

        if (bottomOfWindow && !this.loading && !this.useCategorySearch) {
          this.range.from = this.range.from + this.rangeSize;
          this.getSearchResults(this.terms, true);
        }
      };
    },
    processSearch(data) {
      this.totalHits = data.totalhits;
      this.trackSearchResult();

      if (data.totalhits === 0) {
        this.hint = hintEnum.NO_RESULT;

        if (this.failedSearchRequests.terms) {
          this.failedSearchRequests.terms.push(this.terms);
        } else {
          this.failedSearchRequests.terms = [this.terms];
        }
        Storage.setLocalStorage(
          "failedSearchRequests",
          this.failedSearchRequests,
          true
        );
        this.checkFailedSearches();
      } else {
        this.hint = hintEnum.EMPTY;
      }

      this.displayedResults = [];
      this.setDisplayResults(data.categoryhits);
      this.setFilters(data.categoryhits);

      this.results = this.displayedResults;
      this.loading = false;
      this.initScroll();
    },
    trackSearchResult() {
      const params = {
        "WT.oss": this.terms.join(" "),
        "WT.oss_r": this.totalHits.toString()
      };

      window.wtTrackPage(params);
    },
    updateSearch(data) {
      this.setDisplayResults(data.categoryhits);
      this.setFilters(data.categoryhits);
      this.results = this.displayedResults;
      this.loading = false;
    },
    setDisplayResults(categoryhits) {
      for (const category of categoryhits) {
        this.displayedResults.push(...category.result.hits.hits);

        if (this.getPotentialArticleNumber() && category.name === "products") {
          this.setArticleNumberSearchResult([...category.result.hits.hits]);
        }
      }
      this.displayedResults.sort((a, b) => b._score - a._score);
    },
    setFilters(categoryhits) {
      this.filters = [];

      for (const category of categoryhits) {
        if (category.label) {
          this.memorizedLabels[category.name] = category.label;
        }

        this.filters.push({
          name: category.name,
          label: this.memorizedLabels[category.name] || category.name,
          hits: category.result.hits.total.value
        });
      }
    },
    emptyResultList() {
      this.terms = [];
      this.displayedResults = [];
      this.totalHits = 0;
      this.results = [];
      this.filters = [];
      this.useCategorySearch = false;
      this.range.from = 0;
    },
    startNewSearch(terms) {
      if (terms.length === 1 && terms[0].length < 2) {
        this.emptyResultList();
        this.hint = hintEnum.MIN_LENGTH;
        return;
      }
      this.range.from = 0;
      this.useCategorySearch = false;
      this.articleNumberSearchResult = {
        articleNumber: null,
        productType: null
      };
      this.getSearchResults(terms);
    },
    getSearchResults(terms, onScroll) {
      this.loading = true;
      this.terms = terms;

      getSearchResultFromUrl(window.combinedSearchUrl, {
        word: terms,
        from: this.range.from,
        size: this.rangeSize,
        container: this.searchContainer,
        disableExternalDocuments: this.disableExternalDocuments
      })
        .then(responseData => {
          if (onScroll) {
            this.updateSearch(responseData);
          } else {
            this.processSearch(responseData);
          }
        })
        .catch(error => console.error(error));
    },
    displayResults(results) {
      this.results.length === results.length
        ? ((this.useCategorySearch = false), this.initScroll())
        : (this.useCategorySearch = true);
      this.displayedResults = results;
    },
    setArticleNumberSearchResult(products) {
      for (const product of products) {
        if (product._source.standardarticle) {
          this.articleNumberSearchResult = {
            articleNumber: this.getPotentialArticleNumber(),
            productType: product._source.producttype
          };
          break;
        }
      }
    }
  },
  mounted() {
    this.checkFailedSearches(true);
    this.initSearch();
  }
};
</script>

<style module lang="scss">
@import "./ResultList.scss";
</style>
